export type CompositionProperty = {
    id?: string;
    propertyName?: string;
    isProteinBase?: boolean;
    isFatBase?: boolean;
    isCarbohydrateBase?: boolean;
    aminoAcidType?: string | null;
    carbohydrateType?: string | null;
    degreeOfSaturation?: string | null;
    isEssentialFattyAcid?: boolean;
    names?: unknown[];
    [key: string]: unknown;
};

export type CompositionTreeNode = {
    id: string;
    code?: string;
    labelKey?: string;
    property?: CompositionProperty;
    badges: string[];
    emptyHintKey?: string;
    children: CompositionTreeNode[];
};

const byName = (items: CompositionProperty[]): Map<string, CompositionProperty> => {
    const map = new Map<string, CompositionProperty>();
    items.forEach((item) => {
        if (item?.propertyName) {
            map.set(String(item.propertyName), item);
        }
    });
    return map;
};

const badgesFor = (item: CompositionProperty): string[] => {
    const badges: string[] = [];
    if (item.aminoAcidType) {
        badges.push(String(item.aminoAcidType));
    }
    if (item.isEssentialFattyAcid) {
        badges.push('efa');
    }
    if (item.degreeOfSaturation) {
        badges.push(String(item.degreeOfSaturation));
    }
    if (item.carbohydrateType) {
        badges.push(String(item.carbohydrateType));
    }
    return badges;
};

const leaf = (item: CompositionProperty): CompositionTreeNode => ({
    id: String(item.id || item.propertyName),
    code: item.propertyName ? String(item.propertyName) : undefined,
    property: item,
    badges: badgesFor(item),
    children: [],
});

const group = (
    id: string,
    labelKey: string,
    children: CompositionTreeNode[],
    emptyHintKey = 'app.page.property.composition.emptyBranch',
): CompositionTreeNode => ({
    id,
    labelKey,
    badges: [],
    emptyHintKey: children.length === 0 ? emptyHintKey : undefined,
    children,
});

/** Property node that owns nested children (ADR §5.4: aggregate → individuals). */
const branch = (
    item: CompositionProperty | undefined,
    id: string,
    labelKey: string,
    children: CompositionTreeNode[],
): CompositionTreeNode => {
    if (item) {
        return {
            ...leaf(item),
            children,
            emptyHintKey: children.length === 0
                ? 'app.page.property.composition.emptyBranch'
                : undefined,
        };
    }
    return group(id, labelKey, children);
};

const pickNamed = (
    map: Map<string, CompositionProperty>,
    names: string[],
): CompositionTreeNode[] => names
    .map((name) => map.get(name))
    .filter((item): item is CompositionProperty => Boolean(item))
    .map(leaf);

const pickWhere = (
    items: CompositionProperty[],
    predicate: (item: CompositionProperty) => boolean,
    excludeNames: Set<string> = new Set(),
): CompositionTreeNode[] => items
    .filter((item) => item.propertyName && !excludeNames.has(String(item.propertyName)))
    .filter(predicate)
    .sort((a, b) => String(a.propertyName).localeCompare(String(b.propertyName)))
    .map(leaf);

const FAT_SUBTOTALS = [
    'saturated-fatty-acids',
    'monounsaturated-fatty-acids',
    'polyunsaturated-fatty-acids',
] as const;

const SUGAR_NAMES = new Set([
    'total-sugars',
    'mono-and-disaccharides',
    'glucose',
    'fructose',
    'galactose',
    'ribose',
    'sucrose',
    'maltose',
    'lactose',
]);

const STARCH_PARTS = new Set(['amylose', 'amylopectin']);
const FIBER_NAMES = new Set(['fiber', 'alimentary-fiber', 'cellulose']);

/**
 * UI projection (ADR §5.4):
 * protein → amino-acid → individuals; albumins/globulins siblings
 * fat → SFA/MUFA/PUFA → matching FA; leftovers
 * carbohydrate → sugars / starch / fiber branches
 */
export const buildMacronutrientCompositionTree = (
    items: CompositionProperty[],
): CompositionTreeNode[] => {
    const map = byName(items);
    const proteinRoot = map.get('protein');
    const fatRoot = map.get('fat');
    const carbRoot = map.get('carbohydrate');

    const aminoAcidLeaves = pickWhere(
        items,
        (item) => Boolean(item.isProteinBase) && Boolean(item.aminoAcidType),
    );
    const aminoAcidBranch = branch(
        map.get('amino-acid'),
        'protein-aa',
        'app.page.property.composition.group.aminoAcids',
        aminoAcidLeaves,
    );

    const proteinFractionLeaves = pickNamed(map, ['albumins', 'globulins']);
    const otherProteinParts = pickWhere(
        items,
        (item) => Boolean(item.isProteinBase) && !item.aminoAcidType,
        new Set(['amino-acid', 'albumins', 'globulins', 'protein']),
    );

    const fatSubtotalSet = new Set<string>(FAT_SUBTOTALS);
    const fatBranches = FAT_SUBTOTALS.map((code) => {
        const degree = code === 'saturated-fatty-acids'
            ? 'saturated'
            : code === 'monounsaturated-fatty-acids'
                ? 'monounsaturated'
                : 'polyunsaturated';
        const children = pickWhere(
            items,
            (item) => Boolean(item.isFatBase)
                && String(item.degreeOfSaturation) === degree
                && !fatSubtotalSet.has(String(item.propertyName)),
        );
        return branch(
            map.get(code),
            `fat-${code}`,
            `app.page.property.composition.group.${code}`,
            children,
        );
    });
    const otherFattyAcids = pickWhere(
        items,
        (item) => Boolean(item.isFatBase)
            && !fatSubtotalSet.has(String(item.propertyName))
            && !item.degreeOfSaturation,
        new Set(['fat']),
    );

    const sugarParents = ['total-sugars', 'mono-and-disaccharides'] as const;
    const sugarParentItem = sugarParents.map((n) => map.get(n)).find(Boolean);
    const sugarLeaves = pickWhere(
        items,
        (item) => Boolean(item.isCarbohydrateBase)
            && SUGAR_NAMES.has(String(item.propertyName))
            && !sugarParents.includes(String(item.propertyName) as typeof sugarParents[number]),
    );
    const sugarsBranch = branch(
        sugarParentItem,
        'carb-sugars',
        'app.page.property.composition.group.sugars',
        sugarLeaves,
    );

    const starchParts = pickWhere(
        items,
        (item) => Boolean(item.isCarbohydrateBase) && STARCH_PARTS.has(String(item.propertyName)),
    );
    const starchBranch = branch(
        map.get('starch'),
        'carb-starch',
        'app.page.property.composition.group.starch',
        starchParts,
    );

    const fiberParts = pickWhere(
        items,
        (item) => Boolean(item.isCarbohydrateBase)
            && FIBER_NAMES.has(String(item.propertyName))
            && item.propertyName !== 'alimentary-fiber'
            && item.propertyName !== 'fiber',
    );
    const fiberRootItem = map.get('alimentary-fiber') || map.get('fiber');
    const fiberBranch = branch(
        fiberRootItem,
        'carb-fiber',
        'app.page.property.composition.group.fiber',
        fiberParts,
    );

    const otherCarbs = pickWhere(
        items,
        (item) => Boolean(item.isCarbohydrateBase)
            && !SUGAR_NAMES.has(String(item.propertyName))
            && !STARCH_PARTS.has(String(item.propertyName))
            && !FIBER_NAMES.has(String(item.propertyName))
            && item.propertyName !== 'starch',
        new Set(['carbohydrate']),
    );

    return [
        {
            id: 'root-protein',
            code: 'protein',
            labelKey: 'app.page.property.composition.root.protein',
            property: proteinRoot,
            badges: [],
            emptyHintKey: proteinRoot ? undefined : 'app.page.property.composition.missingRoot',
            children: [
                aminoAcidBranch,
                ...proteinFractionLeaves,
                ...(otherProteinParts.length
                    ? [group(
                        'protein-other',
                        'app.page.property.composition.group.otherProtein',
                        otherProteinParts,
                    )]
                    : []),
            ],
        },
        {
            id: 'root-fat',
            code: 'fat',
            labelKey: 'app.page.property.composition.root.fat',
            property: fatRoot,
            badges: [],
            emptyHintKey: fatRoot ? undefined : 'app.page.property.composition.missingRoot',
            children: [
                ...fatBranches,
                ...(otherFattyAcids.length
                    ? [group(
                        'fat-fa-other',
                        'app.page.property.composition.group.fattyAcids',
                        otherFattyAcids,
                    )]
                    : []),
            ],
        },
        {
            id: 'root-carbohydrate',
            code: 'carbohydrate',
            labelKey: 'app.page.property.composition.root.carbohydrate',
            property: carbRoot,
            badges: [],
            emptyHintKey: carbRoot ? undefined : 'app.page.property.composition.missingRoot',
            children: [
                sugarsBranch,
                starchBranch,
                fiberBranch,
                ...(otherCarbs.length
                    ? [group(
                        'carb-other',
                        'app.page.property.composition.group.otherCarb',
                        otherCarbs,
                    )]
                    : []),
            ],
        },
    ];
};
